import React, { useCallback, useContext, useState } from 'react';
import { Box, InputBase, Stack, Typography } from '@mui/material';
import {
  useMaestroCaption,
  useMaestroAction,
  useMaestroFormData,
  MaestroHeaderView,
  MaestroCaptionNavView,
  MaestroCaptionInputsView,
  MaestroActionButtonsView,
  MaestroPressStatusView,
  MaestroFormView,
  MaestroDebugContext,
} from '@ae/maestrov2-form-lib';
import { setAtPathImmutable } from '@ae/maestrov2-form-lib/transforms';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ArrowCircleRightOutlinedIcon from '@mui/icons-material/ArrowCircleRightOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { pickPrimaryActions } from './pickPrimaryActions';
import { summariseCaptionData } from './summariseCaptionData';
import DeveloperPanel from './DeveloperPanel';


// Blue is a non-action highlight in the Figma system. The lighter value is the
// explicit selected-caption fill; the focus overlay is for editing/focus only.
const HIGHLIGHT = '#4FC3F7';
const FOCUS_SOFT = 'rgba(128,211,249,0.12)';
const FEEDBACK_SURFACE = '#2C2C2C';

/*
 * The three actions keep a traffic-light identity, drawn as the designer
 * returned them: a dark face, a coloured 1px edge and a coloured icon, rather
 * than a solid fill. Colour and glyph carry the identity; the label stays
 * readable on the dark face.
 *
 * Hues are the design system's own success / warning / error values. The
 * returned design covered Animate in and Animate out only, so Continue is
 * built in the same language on the system's warning hue.
 *
 * Only the stop label is tinted. Everything else is neutral, so the one
 * action whose misfire costs most is the one that reads differently.
 */
const ACTIONS = {
  in: { hue: '#21C757', label: '#E8E8E8' },
  continue: { hue: '#FF9513', label: '#E8E8E8' },
  out: { hue: '#FF6552', label: '#F2BDB7' },
};

const ACTION_FACE = '#262626';

// The glyphs the returned design used: a circled tick to go, a circled
// warning to stop. Continue advances, so it takes the circled arrow.
const ICONS = {
  in: CheckCircleOutlineIcon,
  continue: ArrowCircleRightOutlinedIcon,
  out: ErrorOutlineIcon,
};

/** Translucent hue, for the states that tint the face rather than fill it. */
const wash = (hex, alpha) => {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
};

/**
 * A button's words: the action's v_actions `name`, upper-cased. Caption-level
 * actions are named with their own id (`MATCH_STATS_IN`), so underscores read
 * as spaces.
 */
const buttonText = (option) => option.label.replace(/_/g, ' ').toUpperCase();

/**
 * One promoted action.
 *
 * `press` is this button's own slice of the library's press state, so the
 * button can answer "did MY command land?" rather than "did something land?".
 *
 * It is never disabled. A command that has just been sent must stay available
 * — a graphic can legitimately be re-fired, and an operator who cannot press
 * again has no recovery from a dropped send. So a double press is answered by
 * SHOWING rather than blocking: the face tints and holds while the command is
 * in flight and briefly after it lands, which is the window in which someone
 * presses twice. A genuine repeat still reaches the broker, whose own guard
 * reports it back as "already sent" and shows here as an amber ring.
 */
function ActionButton({ tone, label, onClick, press }) {
  const { hue, label: ink } = ACTIONS[tone];
  const mine = press?.action && press.action === press.forAction;
  const kind = mine ? press.kind : 'idle';
  const settled = mine && press.rest;

  const Icon = ICONS[tone];
  const inFlight = kind === 'sending';
  const landed = kind === 'sent' && !settled;
  const refused = kind === 'already' && !settled;
  const failed = kind === 'failed' && !settled;

  let face = ACTION_FACE;
  if (inFlight) face = wash(hue, 0.14);
  else if (landed) face = wash(hue, 0.22);
  else if (failed) face = wash(ACTIONS.out.hue, 0.22);

  // An extra edge drawn inside, so no state changes the button's size.
  let ring = null;
  if (refused) ring = `inset 0 0 0 2px ${ACTIONS.continue.hue}`;
  else if (failed) ring = `inset 0 0 0 2px ${ACTIONS.out.hue}`;
  else if (landed) ring = `inset 0 0 0 2px ${hue}`;

  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      data-press={kind}
      aria-busy={inFlight || undefined}
      sx={{
        flex: 1,
        minWidth: 0,
        minHeight: 68,
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        px: 2.25,
        font: 'inherit',
        fontSize: '1.15rem',
        fontWeight: 600,
        letterSpacing: '0.06em',
        textAlign: 'left',
        cursor: 'pointer',
        borderRadius: '6px',
        border: `1px solid ${hue}`,
        color: ink,
        bgcolor: face,
        boxShadow: ring || 'none',
        transition: 'background-color 140ms, box-shadow 140ms',
        '&:hover': { bgcolor: landed || inFlight ? face : wash(hue, 0.1) },
        '&:active': { bgcolor: wash(hue, 0.18) },
        '&:focus-visible': { outline: '3px solid #80D3F9', outlineOffset: 2 },
      }}
    >
      <Icon aria-hidden sx={{ color: hue, fontSize: 26, flexShrink: 0 }} />
      <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {label}
      </Box>
    </Box>
  );
}

/**
 * Copilot's look, over the library's navigator.
 *
 * The navigator is the library's because the caption tree is the library's —
 * levels, children and DDS-driven options are more than a host can restate.
 * Its appearance is another matter: it is sized for the drive panel, and this
 * window is read across a gallery.
 *
 * Done as a descendant rule rather than a fork of the component. The library
 * styles those controls with `sx`, which lands as a single class; a rule from
 * this wrapper is two classes and wins on specificity, so the fragment is
 * redressed without being reimplemented and keeps every fix the library makes.
 *
 * Selection is marked by the library as `contained` + `success` — green, the
 * colour an action button wears. Choosing is not firing, so it is overridden
 * to the blue used for every other non-action highlight in this window.
 */
// One choice, and one chosen: the navigator's options and the inputs' option
// groups wear the same.
const CHOICE_SX = {
  flex: '1 1 0',
  minWidth: 150,
  minHeight: 56,
  px: 2,
  py: 1,
  fontSize: '1rem',
  fontWeight: 500,
  textTransform: 'none',
  borderRadius: '4px',
  border: '1px solid rgba(255,255,255,0.18)',
  color: 'rgba(255,255,255,0.55)',
  backgroundColor: 'transparent',
  '&:hover': {
    backgroundColor: 'rgba(255,255,255,0.05)',
    borderColor: 'rgba(255,255,255,0.3)',
  },
  '&:focus-visible': { outline: '3px solid #80D3F9', outlineOffset: 2 },
};

const CHOSEN_SX = {
  fontWeight: 600,
  color: HIGHLIGHT,
  borderColor: HIGHLIGHT,
  backgroundColor: FOCUS_SOFT,
  boxShadow: 'none',
  '&:hover': { backgroundColor: FOCUS_SOFT, borderColor: HIGHLIGHT, boxShadow: 'none' },
};

// A dropdown, sized to be hit like a choice.
const FIELD_SX = {
  '& .MuiOutlinedInput-root': {
    width: '100%',
    minHeight: 56,
    fontSize: '1rem',
    borderRadius: '4px',
  },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.18)' },
  '& .MuiSelect-select': { py: 1, px: 2 },
};

// A level's or a field's title: "Caption Type", "Table Range".
const LABEL_SX = {
  fontSize: '0.78rem',
  fontWeight: 600,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  color: 'rgba(255,255,255,0.45)',
  mb: 1,
};

export const NAV_SX = {
  // The level's own title: "Caption Type", "Stat", "Select Team".
  '& .MuiTypography-root': LABEL_SX,
  // The library lays the options out with a flex Box; only its gap is ours.
  '& .MuiBox-root': { gap: 1 },
  '& .MuiButton-root': CHOICE_SX,
  '& .MuiButton-contained': CHOSEN_SX,
  /*
   * Four options or more and the library renders a Select instead of buttons.
   * It cannot be made into buttons from out here — that threshold is inside
   * the component — so it is sized to be hit rather than left at panel scale.
   */
  ...FIELD_SX,
};

/*
 * An option group: a Stack of at most three buttons. The library draws a
 * choice as buttons only below four options (a Select above), so a longer
 * row of buttons is a widget's own controls - Added Time's ten minute
 * buttons, the developer press strip - and keeps its size.
 */
const OPTION_GROUP = '.MuiStack-root:not(:has(> :nth-child(4)))';

/**
 * The caption's inputs at the navigator's size. Their option groups and
 * dropdowns are the same MUI parts the navigator uses, drawn at panel scale.
 */
export const INPUTS_SX = {
  // A field's label is the text right before its option group or dropdown;
  // it wears the navigator's level title, so the window has one label style.
  '& .MuiTypography-body1:has(+ .MuiStack-root, + .MuiInputBase-root)': LABEL_SX,
  [`& ${OPTION_GROUP}:has(> .MuiButton-root)`]: { gap: 1 },
  [`& ${OPTION_GROUP} > .MuiButton-root`]: CHOICE_SX,
  [`& ${OPTION_GROUP} > .MuiButton-contained`]: CHOSEN_SX,
  ...FIELD_SX,
};


// Stable fallbacks: a fresh closure per render would loop useSyncExternalStore.

const CHIP_SX = {
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  borderRadius: '3px',
  px: 0.75,
  py: 0.25,
};

/**
 * The Copilot caption window's arrangement.
 *
 * Renders inside MaestroProvider, so every fragment below is driven by the same
 * engine the standard chrome uses — this file decides only what appears and
 * where. `profile` says which fragments render; see copilotChromeProfiles.js.
 *
 * The action buttons are rendered here rather than by MaestroActionButtonsView
 * because the operator needs words, and that fragment renders icons for
 * IN/OUT. Dispatch still goes through the library's handleAction, so nothing
 * about the command path changes.
 *
 * Choosing and correcting take the same approach: the choices row calls the
 * library's own handleNavChange, and an overtyped value is written with
 * setRootFormData, the setter the form itself uses. Neither adds a path of its
 * own.
 */
export default function CopilotLayout({
  profile,
  graphicName,
  graphicSubtitle,
  graphicId,
  showDeveloperStrip = false,
}) {
  const { selectedCaption, activeCaptionInputs, activeWidget } = useMaestroCaption();
  const { actionEnumOptions, handleAction, pressView } = useMaestroAction();
  const { rootFormData, setRootFormData, editedPaths } = useMaestroFormData();
  // Developer-only: reveals an inputs fragment the profile has switched off.
  const devSettings = useContext(MaestroDebugContext)?.uiSettings;
  const showHiddenInputs = Boolean(devSettings?.showHiddenInputs);

  const [editing, setEditing] = useState(null);

  const { animateIn, continueAction, animateOut } = pickPrimaryActions(actionEnumOptions);
  const inputsShown = profile.showInputs && Boolean(activeWidget || activeCaptionInputs);

  /*
   * Off by default: the window identifies the caption by name and shows what
   * it is about to do, and the values themselves are read off the preview.
   * The rows, and the overtype on them, are one profile key away.
   */
  const summary = profile.showSummary && !inputsShown
    ? summariseCaptionData(rootFormData, selectedCaption)
    : [];

  /*
   * Which option is current comes from navLevels, not from selectedCaption.
   * On a tree deeper than one level they are different things: the root option
   * is a caption TYPE ("Free Text") while selectedCaption is the leaf it
   * resolved to ("FreeText_Attendance"), so comparing them marks nothing.
   * selectedCaption is the fallback for a one-level tree, where they agree.
   */

  /*
   * The words on the grid button the operator pressed, which travel with the
   * invocation. They beat the caption id: a tile reading "Added Time" should
   * not open a window headed "AdditionalTime".
   */
  const title = graphicName || selectedCaption || graphicId || '';

  const beginEdit = (row) => setEditing({ path: row.path, draft: row.value, original: row.value });

  const commitEdit = useCallback(() => {
    setEditing((current) => {
      if (!current) return null;
      if (current.draft !== current.original) {
        setRootFormData(setAtPathImmutable(rootFormData, current.path, current.draft));
      }
      return null;
    });
  }, [rootFormData, setRootFormData]);

  /*
   * Only the operator's own edits are marked. A row is not labelled for being
   * bound: every value here is bound, so the badge appeared on nearly all of
   * them and marked nothing out.
   */
  const rowChip = (path) =>
    editedPaths?.includes(path) ? (
      <Box component="span" sx={{ ...CHIP_SX, color: HIGHLIGHT, border: '1px solid rgba(79,195,247,0.45)', bgcolor: FOCUS_SOFT }}>
        edited
      </Box>
    ) : null;

  const pressIsIdle = !pressView || pressView.kind === 'idle';

  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/*
        Where the settings cog used to sit. Revealed by Ctrl+Alt+D, which is
        bound in the chrome so it survives the window closing; what the
        switches turn on floats in the developer dock.
      */}
      {showDeveloperStrip && <DeveloperPanel graphicId={graphicId} />}

      {profile.showHeader && <MaestroHeaderView hideSettings />}


      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
        {/* The heading names what goes to air; the line under it, when there is
            one, names its parts (a Double stat's two stats). */}
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" sx={{ fontSize: 26, fontWeight: 600, lineHeight: 1.15 }}>
            {title}
          </Typography>
          {graphicSubtitle && (
            <Typography sx={{ mt: 0.5, fontSize: 16, fontWeight: 500, lineHeight: 1.3, color: 'rgba(255,255,255,0.6)' }}>
              {graphicSubtitle}
            </Typography>
          )}
        </Box>
      </Stack>

      {/*
        Mounted whether or not it is shown, for the same reason the action
        fragment is: the auto-selection lives inside it. CaptionNav reads
        `x-autoSelectIndex` (absent means take the first option) and descends
        the default chain, including the DDS levels a host cannot resolve.
        Without it mounted, drive picks a caption on open and Copilot asks the
        operator to pick one that was never in doubt.
      */}
      {/*
        Always mounted, normally never shown. Copilot draws its own choices row
        below; this is here for what the component DOES rather than what it
        renders — auto-selection and the DDS chain descent. Two caption pickers
        in one window is not something to put in front of an operator.

        The developer strip can un-hide it to compare the two directly, which
        is how a caption whose tree Copilot does not yet cover gets spotted.
      */}
      {/*
        The library's own navigator is the baseline for what gets offered.
        A host-side replacement only ever understood a static root enum, which
        is a small part of what a caption tree actually asks: eight of
        gfx_PermScore's nineteen root options carry a child level, and most of
        those are `x-enum` built from live DDS data, some three levels deep.
        Reimplementing that here would duplicate the library and drift from it,
        and a shortcut aimed at a branch rather than a leaf would strand the
        operator with nothing to choose and nothing to fire.

        Hidden rather than unmounted when switched off, because the component
        is also what runs auto-selection and the DDS chain descent.
      */}
      <Box
        data-testid="library-nav"
        aria-hidden={profile.showNavigator ? undefined : 'true'}
        sx={profile.showNavigator ? NAV_SX : { display: 'none' }}
      >
        <MaestroCaptionNavView />
      </Box>

      <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2, ...INPUTS_SX }}>
          {inputsShown && <MaestroCaptionInputsView />}

          {/*
            What the profile is suppressing. A graphic set to hide its inputs
            gives the operator no way to tell an empty caption from a hidden
            one; this says which. Nothing here means the caption authors no
            inputs, and a full box means Copilot is holding them back.
          */}
          {showHiddenInputs && !inputsShown && (
            <Box
              data-testid="hidden-inputs"
              sx={{ border: '1px dashed rgba(226,134,27,0.5)', borderRadius: '4px', p: 1 }}
            >
              <Typography
                sx={{
                  fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.1em',
                  textTransform: 'uppercase', color: '#E2861B', mb: 0.5,
                }}
              >
                Hidden inputs (developer)
              </Typography>
              <MaestroCaptionInputsView />
            </Box>
          )}

          {summary.length > 0 && (
            <Stack component="dl" sx={{ m: 0, gap: 0 }}>
              {summary.map((row) => (
                <Stack
                  key={row.path}
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  spacing={2}
                  sx={{ py: 1, borderBottom: '1px solid rgba(255,255,255,0.09)' }}
                >
                  <Typography component="dt" sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.95rem' }}>
                    {row.label}
                  </Typography>
                  <Box component="dd" sx={{ m: 0, display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                    {rowChip(row.path)}
                    {editing?.path === row.path ? (
                      <InputBase
                        autoFocus
                        value={editing.draft}
                        inputProps={{ 'aria-label': row.label, style: { textAlign: 'right' } }}
                        onChange={(e) => setEditing((c) => ({ ...c, draft: e.target.value }))}
                        onBlur={commitEdit}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') { e.preventDefault(); commitEdit(); }
                          if (e.key === 'Escape') { e.stopPropagation(); setEditing(null); }
                        }}
                        sx={{
                          fontSize: '1rem', fontWeight: 600, color: 'inherit',
                          border: `1px solid ${HIGHLIGHT}`, borderRadius: '3px',
                          bgcolor: FOCUS_SOFT, px: 1, py: 0.25,
                        }}
                      />
                    ) : (
                      <Box
                        component="button"
                        type="button"
                        title="Double-click to overtype"
                        onDoubleClick={() => beginEdit(row)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') { e.preventDefault(); beginEdit(row); }
                        }}
                        sx={{
                          font: 'inherit', fontSize: '1rem', fontWeight: 600, textAlign: 'right',
                          color: 'inherit', background: 'none', borderRadius: '3px', cursor: 'text',
                          border: '1px solid transparent', px: 1, py: 0.625,
                          '&:hover': { borderColor: 'rgba(255,255,255,0.16)', bgcolor: 'rgba(255,255,255,0.04)' },
                        }}
                      >
                        {row.value}
                      </Box>
                    )}
                  </Box>
                </Stack>
              ))}
            </Stack>
          )}

      </Box>

      {/*
        The caption's own choices sit directly above the actions: the operator
        reads what will go to air, then presses. Fixed at full width so the
        buttons below never move between renders — muscle memory is the point.
      */}

      {profile.showActions && (
        <>
          <Stack direction="row" spacing={1.25}>
            {animateIn && (
              <ActionButton
                tone="in"
                label={buttonText(animateIn)}
                onClick={() => handleAction(animateIn.value)}
                press={{ ...pressView, forAction: animateIn.value }}
              />
            )}
            {continueAction && (
              <ActionButton
                tone="continue"
                label={buttonText(continueAction)}
                onClick={() => handleAction(continueAction.value)}
                press={{ ...pressView, forAction: continueAction.value }}
              />
            )}
            {animateOut && (
              <ActionButton
                tone="out"
                label={buttonText(animateOut)}
                onClick={() => handleAction(animateOut.value)}
                press={{ ...pressView, forAction: animateOut.value }}
              />
            )}
          </Stack>
          {/*
            What happened to the press the operator just made — sent, failed,
            or already sent. The chip above reports the LAYER's state, so
            without this a failed send looks identical to a successful one.
          */}
          <Box
            data-testid="copilot-feedback-panel"
            sx={{
              minHeight: 44,
              display: 'flex',
              alignItems: 'center',
              minWidth: 0,
              px: 1.5,
              py: 1,
              bgcolor: FEEDBACK_SURFACE,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              '& [data-kind="sending"]': { color: 'info.main' },
              // Read at a glance straight after a press.
              '& .MuiTypography-caption': { fontSize: 14 },
            }}
          >
            {pressIsIdle && (
              <Box
                role="status"
                aria-live="polite"
                sx={{ display: 'flex', alignItems: 'center', gap: 0.875, color: 'text.secondary' }}
              >
                <Box
                  component="span"
                  aria-hidden
                  sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.38)' }}
                />
                <Typography variant="caption">Ready to send</Typography>
              </Box>
            )}
            <Box sx={{ display: pressIsIdle ? 'none' : 'block', minWidth: 0 }}>
              <MaestroPressStatusView />
            </Box>
          </Box>
        </>
      )}

      {/*
        The action fragment is hidden, never unmounted. Its Chyron numpad
        handler is a useEffect inside it, so dropping it would silently stop
        Numpad − ✳ . ⏎ working — and those keys are now the only way to reach
        Reset, Continue and Apply Data from this window.
      */}
      <Box sx={{ display: 'none' }} aria-hidden="true">
        <MaestroActionButtonsView />
      </Box>

      {profile.showForm && <MaestroFormView />}
    </Box>
  );
}
